import {
  isAcceptedInsuranceFacetPageType,
  isInsurancePageType,
  isLocationPageType,
  isFacetPageType
} from '../../useCases/pageTypeCheck'
import { Location } from '../../../types/models/Location.model'
import { PageType, Page, FacetPageT } from '@/types/models/PageType.model'
import generateLocationPageTitle from './generateLocationPageTitle'
interface GeneratePageTitleParams {
  title: string
  year: number
  term: string
  pageType: PageType
  customTitle?: string
  count: number
  shouldDefaultRegionalize: boolean
  location?: Location
}

interface GeneratePageTitle {
  (params: GeneratePageTitleParams): string
}

const MINIMUM_DISPLAYABLE_CENTER_COUNT = 4
const MAXIMUM_DISPLAYABLE_CENTER_COUNT = 50

const generatePageTitle: GeneratePageTitle = ({
  title,
  year,
  term,
  pageType,
  customTitle,
  count,
  shouldDefaultRegionalize,
  location
}) => {
  const titles = {
    [Page.LOCATION]: generateLocationPageTitle({ location, year }),
    [Page.SETTING]: `Best Rehab Centers with ${title} of ${year} (with Pricing)`,
    [Page.THERAPY]: `Best Rehab Centers with ${title} (Insurance & Pricing)`,
    [Page.SPECIAL_CONSIDERATION]: `Best Rehab Centers with ${title} (Insurance & Pricing)`,
    [Page.APPROACH]: `Best ${title} Centers (Insurance & Pricing)`,
    [Page.AMENITIES]: `Best Rehab Centers with ${title} of ${year} (with Pricing)`,
    [Page.ACTIVITY]: `Rehab Centers with ${title} of ${year} (with Pricing)`,
    [Page.CLIENTELE]: `Best Rehab Centers Treating ${title} (Insurance & Pricing)`,
    [Page.CONDITION]: `Best ${title} Rehab Centers of ${year} (with Pricing)`,
    [Page.CARE]: `Best ${title} Centers (Insurance & Pricing)`,
    [Page.INSURANCE]: `Find Rehabs that Accept ${title} Insurance`,
    [Page.PRICE]: `Best ${title} Rehabs of ${year} with Pricing`,
    [FacetPageT(
      Page.CLIENTELE
    )]: `Best ${title} ${term} Rehab Centers (with Pricing)`,
    [FacetPageT(
      Page.SETTING
    )]: `Best ${title} ${term} Rehab Centers (with Pricing)`,
    [FacetPageT(
      Page.SPECIAL_CONSIDERATIONS
    )]: `Best ${title} ${term} Rehabs (With Pricing)`,
    [FacetPageT(Page.CONDITION)]: `Best ${title} ${term} Rehabs (With Pricing)`,
    [FacetPageT(Page.PRICE)]: `Best ${term} Rehabs in ${title} (With Pricing)`,
    [FacetPageT(
      Page.APPROACHES
    )]: `Best ${title} ${term} Rehabs (With Pricing)`,
    [FacetPageT(
      Page.ACTIVITIES
    )]: `Best ${title} ${term} Rehabs (With Pricing)`,
    [FacetPageT(Page.THERAPIES)]: `Best ${title} ${term} Rehabs (With Pricing)`,
    [FacetPageT(
      Page.AMENITIES
    )]: `Best Rehabs with ${term} in ${title} (With Pricing)`,
    [FacetPageT(Page.CARE)]: `Best ${term} in ${title} (With Pricing)`,
    [FacetPageT(
      Page.INSURANCE
    )]: `Find Rehabs that Accept ${term} Insurance in ${title}`
  }

  const termSpecificTitles = {
    luxury: {
      [Page.AMENITIES]: `Best Luxury Rehab Centers of ${year}`,
      [`${Page.FACET_LOCATION}${Page.AMENITIES}`]: `Best Luxury Rehab Centers in ${title}`
    },
    'eating disorders': {
      [Page.CONDITION]: `Best Eating Disorder Treatment Centers of ${year}`,
      [`${Page.FACET_LOCATION}${Page.CONDITION}`]: `Best ${title} Eating Disorder Treatment Centers (Insurance and Pricing)`
    },
    depression: {
      [Page.CONDITION]: `Best Depression Treatment Centers of ${year}`,
      [`${Page.FACET_LOCATION}${Page.CONDITION}`]: `Best ${title} Depression & Mental Health Treatment Centers`
    }
  }

  const metaTitle = titles[pageType]

  const isLocationPage = isLocationPageType(pageType)
  const isNotLocationPage = !isLocationPage
  const isNotInsurancePage =
    !isInsurancePageType(pageType) &&
    !isAcceptedInsuranceFacetPageType(pageType)
  const isNotLocationOrInsurancePage = isNotLocationPage && isNotInsurancePage
  const isLocationPageWithoutCustomTitle = isLocationPage && !customTitle
  const isNotLocationOrInsurancePageAndHasMetaTitle =
    isNotLocationOrInsurancePage && metaTitle

  let titleToRender = metaTitle

  const adjustedCount = adjustCountForDisplay(count)

  if (isLocationPageWithoutCustomTitle) {
    titleToRender = addCountToTitle(titleToRender, adjustedCount)
  }

  if (isNotLocationOrInsurancePageAndHasMetaTitle) {
    titleToRender = addCountToTitle(metaTitle, adjustedCount)
  }

  if (!isFacetPageType(pageType) && customTitle) {
    titleToRender = customTitle
  }

  const termSpecificTitle = (termSpecificTitles[term.toLowerCase()] ||
    termSpecificTitles[title.toLowerCase()])?.[pageType]

  if (termSpecificTitle) {
    if (count <= MINIMUM_DISPLAYABLE_CENTER_COUNT) {
      titleToRender = termSpecificTitle
    }

    if (
      count > MINIMUM_DISPLAYABLE_CENTER_COUNT &&
      count <= MAXIMUM_DISPLAYABLE_CENTER_COUNT
    ) {
      titleToRender = `${count} ${termSpecificTitle}`
    }

    if (count > MAXIMUM_DISPLAYABLE_CENTER_COUNT) {
      titleToRender = `${MAXIMUM_DISPLAYABLE_CENTER_COUNT} ${termSpecificTitle}`
    }
  }

  if (shouldDefaultRegionalize) {
    return titleToRender
      ?.replace('Centers', 'Centres')
      ?.replace('Center', 'Centre')
  }

  return titleToRender
}

const adjustCountForDisplay = (count: number): number => {
  if (count > MAXIMUM_DISPLAYABLE_CENTER_COUNT) {
    return MAXIMUM_DISPLAYABLE_CENTER_COUNT
  }
  if (count > MINIMUM_DISPLAYABLE_CENTER_COUNT) {
    return count
  }
  return NaN
}

const addCountToTitle = (title: string, count: number): string =>
  count ? `${count} ${title}` : title

export default generatePageTitle
